import { observer } from 'mobx-react-lite';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridType } from '../../../pages/Timetable/Plan/TimetableVersion/Board/types';
import { cardRoomSupply } from '../PreviewMap/previewMapRows';
import { useTimetableStore } from '../TimetableProvider';
import { ResourceTimetable, ResourceTimetableKind, TimetableResource } from './ResourceTimetable';
import styles from './TimetablePreview.module.scss';

const byName = (a: TimetableResource, b: TimetableResource) =>
  a.name.localeCompare(b.name, undefined, { numeric: true });

/**
 * Die zwei Vorschau-Panels zeigen immer die beiden Entitäten, die NICHT der aktuelle Board-Kontext
 * sind (classes → Lehrer + Raum, teachers → Klasse + Raum, rooms → Klasse + Lehrer).
 */
const kindsForContext = (context: GridType): [ResourceTimetableKind, ResourceTimetableKind] => {
  if (context === 'teachers') return ['class', 'room'];
  if (context === 'rooms') return ['class', 'teacher'];
  return ['teacher', 'room'];
};

/**
 * Ein eigenständiges Panel der Stundenplan-Vorschau – Klassen-, Lehrer- oder Raumplan der Karte in
 * der Hand bzw. (verzögert) unter dem Cursor. Sobald eine Karte aufgenommen/gehovert wird, zeigt das
 * Panel den Wochen-Stundenplan (bei mehreren Ressourcen per Tab). Nur wenn eine Karte in der Hand
 * ist UND der Board-Kontext eine zeitliche Steckung erlaubt (nicht im Raum-Kontext), ist es zugleich
 * Ablageziel. Ergänzt die PreviewMap, ersetzt sie nicht.
 */
const TimetablePreviewPanel: FC<{ kind: ResourceTimetableKind }> = observer(({ kind }) => {
  const store = useTimetableStore();
  const { t } = useTranslation();

  // Zielkarte wie in der CardInfo: Karte in der Hand hat Vorrang, sonst die gepinnte bzw. (verzögert)
  // gehoverte Karte – auch aus dem Stapel. Nur mit Karte in der Hand ist der Plan Ablageziel; im
  // Raum-Kontext bewegt das Board keine Zeit, dort bleibt die Vorschau rein informativ.
  const picked = store.pickedCard;
  const pickedCard = picked ? store.getCardByUuid(picked.cardUuid) : undefined;
  const hoverCard = store.pinnedCard || store.hoveredCard ? store.getCardToShowOnInfo() : undefined;
  const card = pickedCard ?? hoverCard;
  const interactive = Boolean(pickedCard) && store.context !== 'rooms';
  const active = Boolean(card);

  const previewHours = useMemo(() => store.hours.filter((hour) => !hour.isPause), [store.hours]);

  const resources: TimetableResource[] = useMemo(() => {
    if (!card) return [];
    if (kind === 'teacher') {
      return card.teachers
        .map((teacher) => ({ uuid: teacher.uuid, name: teacher.displayNameShort ?? teacher.shortName }))
        .sort(byName);
    }
    if (kind === 'class') {
      return card.classes
        .map((schoolClass) => ({ uuid: schoolClass.uuid, name: schoolClass.shortName ?? schoolClass.name }))
        .sort(byName);
    }
    // Raumplan zeigt den Raumvorrat; die der Karte TATSÄCHLICH zugewiesenen Räume (live aus
    // cardRooms, mehrere möglich) werden markiert und einer davon vorausgewählt.
    const assigned = new Set(Array.from(store.cardRooms.get(card.uuid)?.values() ?? []).map((room) => room.value));
    if (assigned.size === 0) card.rooms.forEach((room) => assigned.add(room.uuid));
    const supply = cardRoomSupply(
      card.uuid,
      card.rooms.map((room) => room.uuid),
      store.defaultCardRooms,
    );
    return Array.from(supply, (uuid) => ({ uuid, name: store.rooms.get(uuid)?.name ?? '', marked: assigned.has(uuid) }))
      .filter((room) => room.name)
      .sort(byName);
  }, [kind, card, store.cardRooms, store.defaultCardRooms, store.rooms]);

  // Raumplan: den zugewiesenen (markierten) Raum vorwählen; sonst erste Ressource.
  const defaultActiveUuid = resources.find((resource) => resource.marked)?.uuid;

  const title = t(kind === 'teacher' ? 'persons.title_neutral' : kind === 'room' ? 'rooms.title' : 'classes.title', {
    count: resources.length,
  });

  // `data-timetable-preview`: der globale Outside-Click-Handler (CardPreview) soll Klicks hier
  // nicht als „Karte ablegen" werten — Zellen und Tabs behandeln ihre Klicks selbst.
  return (
    <div className={styles['timetable-preview']} data-timetable-preview=''>
      {active && card ? (
        <ResourceTimetable
          kind={kind}
          title={title}
          resources={resources}
          defaultActiveUuid={defaultActiveUuid}
          hours={previewHours}
          cardUuid={card.uuid}
          duration={picked?.duration ?? card.duration ?? 1}
          interactive={interactive}
        />
      ) : (
        <div className={styles.hint}>{t('pinboard.timetablePreview.hint')}</div>
      )}
    </div>
  );
});

TimetablePreviewPanel.displayName = 'TimetablePreviewPanel';

export const TimetablePreviewFirst: FC = observer(() => {
  const store = useTimetableStore();
  return <TimetablePreviewPanel kind={kindsForContext(store.context)[0]} />;
});
TimetablePreviewFirst.displayName = 'TimetablePreviewFirst';

export const TimetablePreviewSecond: FC = observer(() => {
  const store = useTimetableStore();
  return <TimetablePreviewPanel kind={kindsForContext(store.context)[1]} />;
});
TimetablePreviewSecond.displayName = 'TimetablePreviewSecond';
